import * as React from 'react';
import { Star, Clock, ChevronDown, Search, X, ArrowRight, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ProjectWithMetadata, ProjectFilters } from '@/types/project-selector';
import { useProjectSelector } from '@/hooks/use-project-selector';
import { ProjectSearchModal } from './ProjectSearchModal';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import type { Project } from '@/types';

interface ProjectSelectorProps {
  projects: Project[] | ProjectWithMetadata[];
  value?: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When false, the Favourites section is hidden in both the dropdown and the full search modal (e.g. approver screens) */
  showFavourites?: boolean;
  // Server-side pagination props
  onLoadMore?: () => void;
  hasMore?: boolean;
  remainingCount?: number;
  isLoadingMore?: boolean;
  // Server-side search props
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  totalCount?: number;
}

export function ProjectSelector({
  projects,
  value,
  onChange,
  disabled,
  placeholder = 'Select project...',
  showFavourites = true,
  // Server-side pagination props
  onLoadMore,
  hasMore,
  remainingCount,
  isLoadingMore,
  // Server-side search props
  onServerSearch,
  isSearching,
  totalCount,
}: ProjectSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite,
  } = useProjectSelector(projects);

  const selectedProject = enrichedProjects.find((p) => p.id === value);

  // Filter projects based on search query
  const filteredProjects = React.useMemo(() => {
    if (!searchQuery.trim()) return enrichedProjects;
    
    const query = searchQuery.toLowerCase();
    return enrichedProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.code.toLowerCase().includes(query) ||
        ('description' in p && p.description && p.description.toLowerCase().includes(query))
    );
  }, [enrichedProjects, searchQuery]);

  // Get filtered favourites and recent
  const filteredFavourites = React.useMemo(() => {
    if (!searchQuery.trim()) return favouriteProjects.slice(0, 5);
    return favouriteProjects.filter((p) =>
      filteredProjects.some((fp) => fp.id === p.id)
    );
  }, [favouriteProjects, filteredProjects, searchQuery]);

  const filteredRecent = React.useMemo(() => {
    if (!searchQuery.trim()) return recentProjects.slice(0, 5);
    return recentProjects.filter((p) =>
      filteredProjects.some((fp) => fp.id === p.id)
    );
  }, [recentProjects, filteredProjects, searchQuery]);

  // Get additional filtered projects (not in favourites or recent)
  const additionalProjects = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const favouriteIds = new Set(filteredFavourites.map((p) => p.id));
    const recentIds = new Set(filteredRecent.map((p) => p.id));
    
    return filteredProjects
      .filter((p) => !favouriteIds.has(p.id) && !recentIds.has(p.id))
      .slice(0, 10);
  }, [filteredProjects, filteredFavourites, filteredRecent, searchQuery]);

  // Handle project selection
  const handleSelectProject = React.useCallback(
    (projectId: string) => {
      // Check if project is archived/inactive
      const project = enrichedProjects.find((p) => p.id === projectId);
      // Handle both Project (active: boolean) and ProjectWithMetadata (status: string) types
      const isArchived = 'status' in project! && project.status === 'archived';
      const isInactive = 'active' in project! && project.active === false;
      if (isArchived || isInactive) {
        alert('Cannot select archived/inactive project. Please choose an active project.');
        return;
      }

      onChange(projectId);
      recordProjectAccess(projectId);
      setDropdownOpen(false);
    },
    [enrichedProjects, onChange, recordProjectAccess]
  );

  // Handle modal open
  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
    setSearchQuery(''); // Reset search when opening modal
  }, []);

  // Trigger server-side search when the inline dropdown search query changes
  // This ensures projects beyond the initial page can be found in the dropdown
  React.useEffect(() => {
    if (!onServerSearch || !dropdownOpen) return;

    const timer = setTimeout(() => {
      onServerSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, onServerSearch, dropdownOpen]);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
      // Reset server-side search to restore the full project list
      if (onServerSearch) {
        onServerSearch('');
      }
    } else {
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen, onServerSearch]);

  return (
    <>
      <Popover open={!disabled && dropdownOpen} onOpenChange={(open) => !disabled && setDropdownOpen(open)}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={
                selectedProject
                  ? `${selectedProject.name} (${selectedProject.code})`
                  : ''
              }
              placeholder={placeholder}
              readOnly
              disabled={disabled}
              className={cn(
                'cursor-pointer pr-10',
                disabled && 'cursor-not-allowed'
              )}
              onClick={() => !disabled && setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  !disabled && setDropdownOpen(true);
                }
                // Ctrl/Cmd + K to open modal
                if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                  e.preventDefault();
                  !disabled && handleOpenModal();
                }
              }}
              aria-label="Select project"
              aria-expanded={dropdownOpen}
              role="combobox"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-8 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <ScrollArea className="max-h-[400px]">
            <ScrollAreaViewport className="max-h-[400px] p-2">
              {/* Favourites Section */}
              {showFavourites && filteredFavourites.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" />
                    FAVOURITES
                  </div>
                  <div className="space-y-0.5">
                    {filteredFavourites.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelectProject(project.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === project.id && 'bg-accent'
                        )}
                      >
                        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">
                            ({project.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recently Used Section */}
              {filteredRecent.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    RECENTLY USED
                  </div>
                  <div className="space-y-0.5">
                    {filteredRecent.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelectProject(project.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === project.id && 'bg-accent'
                        )}
                      >
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">
                            ({project.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Search Results */}
              {searchQuery && additionalProjects.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Search className="h-3.5 w-3.5" />
                    OTHER RESULTS
                  </div>
                  <div className="space-y-0.5">
                    {additionalProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelectProject(project.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === project.id && 'bg-accent'
                        )}
                      >
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">
                            ({project.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Searching indicator */}
              {searchQuery && isSearching && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-50" />
                  <p>Searching...</p>
                </div>
              )}

              {/* No Results Message */}
              {searchQuery && !isSearching && filteredFavourites.length === 0 && filteredRecent.length === 0 && additionalProjects.length === 0 && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No projects found matching "{searchQuery}"</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              )}

              {/* Empty State (no search) */}
              {!searchQuery && filteredRecent.length === 0 && (showFavourites ? filteredFavourites.length === 0 : true) && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <p>{showFavourites ? 'No favourites or recent projects' : 'No recently used projects'}</p>
                  <p className="text-xs mt-1">Use the search above or click "Show More"</p>
                </div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical">
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>

          <Separator className="my-0" />
          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full justify-center text-sm"
              onClick={handleOpenModal}
            >
              Show More...
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <ProjectSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        projects={enrichedProjects.map(p => ({
          ...p,
          // Map Project's active boolean to ProjectWithMetadata's status string
          // Normalize status to lowercase to ensure consistent filtering (API may return "Active" vs "active")
          status: 'status' in p ? String(p.status).toLowerCase() : (p.active ? 'active' : 'archived'),
          // Ensure description exists (optional in Project)
          description: 'description' in p ? p.description : undefined,
        })) as ProjectWithMetadata[]}
        value={value}
        onSelect={handleSelectProject}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        showFavourites={showFavourites}
        // Server-side pagination props
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        remainingCount={remainingCount}
        isLoadingMore={isLoadingMore}
        // Server-side search props
        onServerSearch={onServerSearch}
        isSearching={isSearching}
        totalCount={totalCount}
      />
    </>
  );
}
